import React, { useMemo, useState } from "react";
import {
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileJson2,
  Globe2,
  Loader2,
  RefreshCcw,
  Search,
  ServerCog,
  UserRound,
} from "lucide-react";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const normalizeWebsiteUrl = (domain) => {
  const cleanDomain = String(domain || "")
    .replace(/^https?:\/\//i, "")
    .replace(/\/+$/, "")
    .trim();

  return cleanDomain ? `https://${cleanDomain}` : "";
};

export default function Websites({
  websites = [],
  websitesLoading = false,
  websiteSyncBusy = {},
  fetchWebsites,
  manuallyUpdateWebsiteManifest,
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const filteredWebsites = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    if (!search) return websites;

    return websites.filter((website) => {
      return (
        String(website?.domain || "")
          .toLowerCase()
          .includes(search) ||
        String(website?.userid || "")
          .toLowerCase()
          .includes(search) ||
        String(website?.id || "")
          .toLowerCase()
          .includes(search)
      );
    });
  }, [websites, searchTerm]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredWebsites.length / pageSize),
  );

  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedWebsites = useMemo(() => {
    const start = (safeCurrentPage - 1) * pageSize;
    return filteredWebsites.slice(start, start + pageSize);
  }, [filteredWebsites, pageSize, safeCurrentPage]);

  const startRecord =
    filteredWebsites.length === 0
      ? 0
      : (safeCurrentPage - 1) * pageSize + 1;

  const endRecord = Math.min(
    safeCurrentPage * pageSize,
    filteredWebsites.length,
  );

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handlePageSize = (event) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  return (
    <div className="rounded-2xl border bg-white p-3">
      <div className="rounded-2xl border bg-gradient-to-br from-emerald-50 via-white to-white p-4">
        {/* Header */}
        <div className="mb-4 flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-semibold text-emerald-700">
              <Globe2 size={14} />
              Website Registry
            </div>

            <h2 className="text-lg font-semibold text-gray-900">
              Deployed Websites
            </h2>

            <p className="mt-1 text-sm text-gray-500">
              View deployed websites and manually synchronize their manifest
              configuration.
            </p>
          </div>

          <button
            type="button"
            onClick={fetchWebsites}
            disabled={websitesLoading}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border bg-white px-4 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCcw
              size={16}
              className={websitesLoading ? "animate-spin" : ""}
            />
            Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
          <StatCard
            icon={<Globe2 size={18} />}
            label="Total Websites"
            value={websites.length}
            helper="Registered website deployments"
          />

          <StatCard
            icon={<ServerCog size={18} />}
            label="Visible Records"
            value={filteredWebsites.length}
            helper="Records matching current search"
          />

          <StatCard
            icon={<FileJson2 size={18} />}
            label="Manual Sync"
            value="Available"
            helper="Run manifest JSON update per website"
          />
        </div>

        {/* Search */}
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-md">
            <Search
              size={17}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />

            <input
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search by website, user ID or record ID..."
              className="h-11 w-full rounded-xl border bg-white pl-10 pr-3 text-sm outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100"
            />
          </div>

          <div className="flex items-center justify-between gap-3 lg:justify-end">
            <p className="text-sm text-gray-500">
              Showing{" "}
              <span className="font-semibold text-gray-800">
                {startRecord}-{endRecord}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-800">
                {filteredWebsites.length}
              </span>
            </p>

            <select
              value={pageSize}
              onChange={handlePageSize}
              className="h-10 rounded-xl border bg-white px-3 text-sm outline-none focus:border-emerald-400"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-2xl border bg-white">
          <div className="max-w-full overflow-x-auto">
            <table className="w-full min-w-[950px] text-left">
              <thead className="border-b bg-gray-50">
                <tr>
                  <Th>#</Th>
                  <Th>Website</Th>
                  <Th>User</Th>
                  <Th>Created</Th>
                  <Th>Updated</Th>
                  <Th align="right">Actions</Th>
                </tr>
              </thead>

              <tbody>
                {websitesLoading ? (
                  <LoadingRows />
                ) : paginatedWebsites.length > 0 ? (
                  paginatedWebsites.map((website, index) => {
                    const websiteId = website?.id;
                    const isSyncing = !!websiteSyncBusy?.[websiteId];
                    const websiteUrl = normalizeWebsiteUrl(website?.domain);
                    const wasSynced =
                      website?.manifest_status === "success";

                    return (
                      <tr
                        key={websiteId || `${website?.domain}-${index}`}
                        className="border-b transition last:border-b-0 hover:bg-emerald-50/40"
                      >
                        <Td>
                          <span className="text-sm font-semibold text-gray-500">
                            {(safeCurrentPage - 1) * pageSize + index + 1}
                          </span>
                        </Td>

                        <Td>
                          <div className="flex items-center gap-3">
                            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-emerald-50 text-emerald-600">
                              <Globe2 size={17} />
                            </div>

                            <div className="min-w-0">
                              <p className="max-w-[320px] truncate font-mono text-sm font-semibold text-gray-900">
                                {website?.domain || "—"}
                              </p>

                              <p className="mt-0.5 text-xs text-gray-400">
                                Website ID: {websiteId || "—"}
                              </p>
                            </div>
                          </div>
                        </Td>

                        <Td>
                          <div className="inline-flex items-center gap-2 rounded-xl border bg-gray-50 px-3 py-2">
                            <UserRound size={14} className="text-gray-400" />

                            <span className="font-mono text-xs font-semibold text-gray-700">
                              {website?.userid || "—"}
                            </span>
                          </div>
                        </Td>

                        <Td>
                          <DateValue value={website?.created_at} />
                        </Td>

                        <Td>
                          <DateValue
                            value={
                              website?.manifest_updated_at ||
                              website?.updated_at
                            }
                            success={wasSynced}
                          />
                        </Td>

                        <Td align="right">
                          <div className="flex items-center justify-end gap-2">
                            {websiteUrl && (
                              <a
                                href={websiteUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex h-9 items-center justify-center gap-2 rounded-xl border bg-white px-3 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
                              >
                                <ExternalLink size={14} />
                                Open
                              </a>
                            )}

                            <button
                              type="button"
                              onClick={() =>
                                manuallyUpdateWebsiteManifest?.(website)
                              }
                              disabled={isSyncing}
                              className="inline-flex h-9 min-w-[150px] items-center justify-center gap-2 rounded-xl bg-[#01C37D] px-3 text-xs font-semibold text-white transition hover:bg-[#00ab6d] disabled:cursor-not-allowed disabled:bg-gray-300"
                            >
                              {isSyncing ? (
                                <>
                                  <Loader2
                                    size={14}
                                    className="animate-spin"
                                  />
                                  Updating JSON...
                                </>
                              ) : wasSynced ? (
                                <>
                                  <CheckCircle2 size={14} />
                                  Update Again
                                </>
                              ) : (
                                <>
                                  <FileJson2 size={14} />
                                  Update
                                </>
                              )}
                            </button>
                          </div>
                        </Td>
                      </tr>
                    );
                  })
                ) : (
                  <EmptyState searchTerm={searchTerm} />
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-gray-500">
            Page{" "}
            <span className="font-semibold text-gray-800">
              {safeCurrentPage}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-gray-800">
              {totalPages}
            </span>
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={safeCurrentPage <= 1}
              onClick={() =>
                setCurrentPage((prev) => Math.max(1, prev - 1))
              }
              className="inline-flex h-9 items-center gap-1 rounded-xl border bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft size={16} />
              Previous
            </button>

            <button
              type="button"
              disabled={safeCurrentPage >= totalPages}
              onClick={() =>
                setCurrentPage((prev) =>
                  Math.min(totalPages, prev + 1),
                )
              }
              className="inline-flex h-9 items-center gap-1 rounded-xl border bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, helper }) {
  return (
    <div className="rounded-2xl border bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 text-emerald-600">
          {icon}
        </div>

        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            {label}
          </p>

          <p className="mt-0.5 truncate text-base font-semibold text-gray-900">
            {value}
          </p>
        </div>
      </div>

      <p className="mt-3 text-xs text-gray-400">{helper}</p>
    </div>
  );
}

function DateValue({ value, success = false }) {
  return (
    <div className="flex items-center gap-2">
      {success ? (
        <CheckCircle2 size={14} className="shrink-0 text-emerald-600" />
      ) : (
        <CalendarDays size={14} className="shrink-0 text-gray-400" />
      )}

      <span
        className={`text-sm ${
          success ? "font-medium text-emerald-700" : "text-gray-600"
        }`}
      >
        {formatDate(value)}
      </span>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th
      className={`whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500 ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function Td({ children, align = "left" }) {
  return (
    <td
      className={`px-4 py-3 align-middle ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {children}
    </td>
  );
}

function LoadingRows() {
  return (
    <>
      {Array.from({ length: 7 }).map((_, index) => (
        <tr key={index} className="border-b last:border-b-0">
          {Array.from({ length: 6 }).map((__, cellIndex) => (
            <td key={cellIndex} className="px-4 py-4">
              <div className="h-8 animate-pulse rounded-xl bg-gray-100" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function EmptyState({ searchTerm }) {
  return (
    <tr>
      <td colSpan={6} className="px-4 py-14 text-center">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-gray-50 text-gray-400">
          <Globe2 size={24} />
        </div>

        <h3 className="mt-4 text-base font-semibold text-gray-900">
          No websites found
        </h3>

        <p className="mt-1 text-sm text-gray-500">
          {searchTerm
            ? "No website matches your current search."
            : "Website records will appear here after refresh."}
        </p>
      </td>
    </tr>
  );
}